/**
 * The RelatedCasesApi module contains APIs that handle the related cases of a case.
 * The following APIs are part of the RelatedCasesApi module:
 * @module RelatedCasesApi
 */
/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import { invokeRestApi } from '../rest-client';

const ADD_RELATED_CASES = 'addRelatedCases';
const REMOVE_RELATED_CASE = 'removeRelatedCase';
const GET_RELATED_CASES = 'getRelatedCases';

/**
 * This API relates several cases to a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API relates two cases whose IDs are T-200 and T-201 to the case whose ID is W-102.</caption>
 * const casesArray =  [{ ID : 'ORG-APP-WORK T-200'}, {ID : 'ORG-APP-WORK T-201'}]
 * PCore.getRelatedCasesApi().addRelatedCases('ORG-APP-WORK W-102', casesArray, 'app/primary_1')
 * .then(() => {
 *   // success
 * }).catch(err => {
 *    // Error handling
 * });
 *
 * @param caseID ID of the case pointing to pzInsKey value to which several cases can be related.
 * @param relatedCases The list of case IDs that must be related to a case.
 * @param context The name of the context from where the API is being called.
 * @returns A Promise that returns the list of related cases added to the case.
 */

const addRelatedCases = (caseID: string, relatedCases: object[], context: string): Promise<object> => {
  const queryPayload = {
    caseID
  };
  const reqBody = JSON.stringify({
    cases: relatedCases
  });
  return invokeRestApi(ADD_RELATED_CASES, { body: reqBody, queryPayload }, context);
};

/**
 * This API removes a related case from its relationship with a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API removes the case whose ID is T-200 from its relationship with the case whose ID is W-102.</caption>
 * PCore.getRelatedCasesApi().removeRelatedCase('ORG-APP-WORK W-102', 'ORG-APP-WORK T-200', 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID         The ID of the case pointing to pzInsKey value from whose relationship the related case must be removed.
 * @param relatedCaseID  The ID of the case that should be removed from the relationship.
 * @param context        The name of the context from where the API is being called.
 */

const removeRelatedCase = (caseID: string, relatedCaseID: string, context: string) => {
  const queryPayload = {
    caseID,
    relatedCaseID
  };
  return invokeRestApi(REMOVE_RELATED_CASE, { queryPayload }, context);
};

/**
 * This API obtains the related cases of a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the related cases of a case whose ID is W-102.</caption>
 * PCore.getRelatedCasesApi().getRelatedCases('ORG-APP-WORK W-102', 'app/primary_1')
 * .then(relatedCases => {
 *    // relatedCases array
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID     The ID of the case pointing to pzInsKey value whose related cases must be obtained.
 * @param context    The name of the context from where the API is being called.
 * @returns A Promise that returns the list of related cases linked to the specified case.
 */

const getRelatedCases = (caseID: string, context: string): Promise<object> => {
  const queryPayload = { caseID };
  return invokeRestApi(GET_RELATED_CASES, { queryPayload }, context).then((response) => response.data.relatedCases);
};

export default {
  addRelatedCases,
  removeRelatedCase,
  getRelatedCases
};
